import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";

import CheckIcon from "@mui/icons-material/Check";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { lighten } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";

import { format } from "date-fns";

import { AVAILABILITY_BLOCK_COLORS_FEATURE } from "constants/feature-flags";
import { Clinic } from "services/types/clinics.type";

type AvailabilitySlotProps = {
  startTime: string;
  selected: boolean;
  color: any;
  isCollapsibleView: boolean;
  onSelection: () => void;
  clinic: Clinic;
  selectedClinicId?: number;
  showClinic?: boolean;
  blockColor?: string | null;
  blockName?: string | null;
};

export default function AvailabilitySlot({
  startTime,
  selected,
  color,
  isCollapsibleView,
  onSelection,
  clinic,
  selectedClinicId,
  showClinic = false,
  blockColor,
  blockName
}: AvailabilitySlotProps) {
  const { palette } = useTheme();
  const flags = useFlags();

  // When the block-colors flag is on and the slot falls within a block, tint
  // the slot with the block's color (lightened to match the existing pastel
  // [200] shade so the dark text stays legible). Otherwise keep today's
  // staff-palette color — zero visual change with the flag off or no block.
  const backgroundColor =
    flags[AVAILABILITY_BLOCK_COLORS_FEATURE] && blockColor
      ? lighten(blockColor, 0.7)
      : color[200];

  const primaryText = (
    <Stack spacing={0.75}>
      <Typography component="div" sx={{ fontSize: "17px" }}>
        {format(new Date(startTime), "EEE, MMM d")}
      </Typography>
      {showClinic && (
        <Typography
          variant="body1"
          component="div"
          fontWeight={selectedClinicId === clinic?.id ? 700 : undefined}
        >
          {clinic.name}
        </Typography>
      )}
    </Stack>
  );
  const secondaryText = (
    <Typography component="div" sx={{ fontSize: "16px", fontWeight: 700 }}>
      {format(new Date(startTime), "h:mmaaa")}
    </Typography>
  );

  return (
    <ListItem
      sx={{ mt: isCollapsibleView ? 0 : 1 }}
      disablePadding
      disableGutters
      secondaryAction={
        selected ? (
          <IconButton
            sx={{ mt: -6, color: palette.lightBackground }}
            size="small"
          >
            <CheckIcon sx={{ width: 16, height: 16 }} />
          </IconButton>
        ) : (
          ""
        )
      }
      data-testid="availability-slot"
    >
      <Tooltip title={blockName || ""} placement="top" arrow>
        <ListItemButton
          sx={{
            p: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: backgroundColor,
            width: "100%",
            "&.Mui-selected": {
              paddingRight: 0,
              backgroundColor: palette.availabilitySlot.bgcolor,
              color: palette.availabilitySlot.color,
              "&:hover": {
                backgroundColor: palette.availabilitySlot.bgcolor,
                opacity: 0.4
              }
            }
          }}
          selected={selected}
          onClick={onSelection}
        >
          <Stack direction="row" spacing={0.5} alignItems="center" ml={-0.25}>
            <ListItemText
              sx={{ py: 0.5, width: "85px" }}
              primary={primaryText}
            />
            <ListItemText sx={{ py: 0.5 }} primary={secondaryText} />
          </Stack>
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
}
